import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { useSync } from '../../../contexts/SyncContext';
import {
  NUTRITION_KEY,
  NUTRITION_GOALS_KEY,
  SAVED_BARCODES_KEY,
  SAVED_MEALS_KEY,
  CUSTOM_MEAL_TIMES_KEY,
  EXTERNAL_STEPS_KEY,
  CUSTOM_PRODUCTS_KEY,
  DEFAULT_GOALS,
  DEFAULT_MEAL_TIMES,
  DEFAULT_BMR_DATA,
  DEFAULT_MANUAL_ENTRY,
} from '../utils/constants';
import { formatDate, getToday, stepsToCalories, calculateBMR, calculateTDEE, calculateDailyTarget } from '../utils/nutritionCalculations';
import { calculateGoalAdjustment, getGoalWeekNumber, convertWeight, convertHeight } from '../../../utils/nutritionCalculators';

const NutritionContext = createContext(null);

export function NutritionProvider({ children, isExpired = false, customMealTimes: propMealTimes }) {
  // Core state
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [entries, setEntries] = useState([]);
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [burnedCalories, setBurnedCalories] = useState(0);
  const [burnedCaloriesOverride, setBurnedCaloriesOverride] = useState(false);
  const [externalSteps, setExternalSteps] = useState(0);
  const [bodyWeight, setBodyWeight] = useState(70);
  const [bmrData, setBmrData] = useState(DEFAULT_BMR_DATA);
  
  // Custom meal times - use prop if available, otherwise use local state
  const [localCustomMealTimes, setLocalCustomMealTimes] = useState([]);
  const [selectedMealTime, setSelectedMealTime] = useState('breakfast');
  
  // Saved data
  const [savedBarcodes, setSavedBarcodes] = useState({});
  const [savedMeals, setSavedMeals] = useState([]);
  const [customProducts, setCustomProducts] = useState([]);
  const [savedMenus, setSavedMenus] = useState([]);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Refs for preventing race conditions
  const burnedCaloriesLoadedForDate = useRef(null);
  
  // Sync context - use startSync, completeSync, failSync from SyncContext
  const { startSync, completeSync, failSync } = useSync();

  // Get all meal times - prioritize prop meal times if provided
  const allMealTimes = propMealTimes && propMealTimes.length > 0 
    ? propMealTimes 
    : (localCustomMealTimes.length > 0 ? localCustomMealTimes : DEFAULT_MEAL_TIMES);

  // ============ DATA SYNC FUNCTIONS ============
  
  const syncNutritionToBackend = useCallback(async (date, entriesData) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;
    
    try {
      startSync();
      await api.put(`/data/nutrition/${date}`, {
        entries: entriesData
      });
      completeSync();
    } catch (error) {
      console.error('Failed to sync nutrition:', error);
      failSync(error);
    }
  }, [startSync, completeSync, failSync]);

  const loadNutritionFromBackend = useCallback(async (date) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return null;
    
    try {
      const response = await api.get(`/data/nutrition/${date}`);
      return response;
    } catch (error) {
      console.error('Failed to load nutrition from backend:', error);
      return null;
    }
  }, []);

  const syncBurnedCaloriesToBackend = useCallback(async (date, calories) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;
    
    try {
      await api.put(`/data/burned-calories/${date}`, { calories });
    } catch (error) {
      console.error('Failed to sync burned calories:', error);
    }
  }, []);

  const loadBurnedCaloriesFromBackend = useCallback(async (date) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return null;
    
    try {
      const response = await api.get(`/data/burned-calories/${date}`);
      return response?.calories || 0;
    } catch (error) {
      console.error('Failed to load burned calories:', error);
      return null;
    }
  }, []);

  const syncExternalStepsToBackend = useCallback(async (date, steps) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;
    
    try {
      await api.put(`/data/external-steps/${date}`, { steps });
    } catch (error) {
      console.error('Failed to sync external steps:', error);
    }
  }, []);

  const loadExternalStepsFromBackend = useCallback(async (date) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return null;
    
    try {
      const response = await api.get(`/data/external-steps/${date}`);
      return response?.steps || 0;
    } catch (error) {
      console.error('Failed to load external steps:', error);
      return null;
    }
  }, []);

  const syncBarcodesToBackend = useCallback(async (barcodes) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;
    
    try {
      await api.put('/data/saved-barcodes', { barcodes });
    } catch (error) {
      console.error('Failed to sync barcodes:', error);
    }
  }, []);

  const loadBarcodesFromBackend = useCallback(async () => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return null;
    
    try {
      const response = await api.get('/data/saved-barcodes');
      return response?.barcodes || {};
    } catch (error) {
      console.error('Failed to load barcodes:', error);
      return null;
    }
  }, []);

  const syncMealsToBackend = useCallback(async (meals) => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;
    
    try {
      await api.put('/data/saved-meals', { meals });
    } catch (error) {
      console.error('Failed to sync meals:', error);
    }
  }, []);

  const loadMealsFromBackend = useCallback(async () => {
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return null;
    
    try {
      const response = await api.get('/data/saved-meals');
      return response?.meals || [];
    } catch (error) {
      console.error('Failed to load meals:', error);
      return null;
    }
  }, []);

  // ============ ENTRY MANAGEMENT ============

  const addEntry = useCallback((entry) => {
    if (isExpired) {
      toast.error('Your trial has expired. Please subscribe to add entries.');
      return false;
    }
    
    const newEntry = {
      ...entry,
      id: `entry_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      // Use entry's mealTime if provided, otherwise fall back to selectedMealTime
      mealTime: entry.mealTime || selectedMealTime,
      timestamp: new Date().toISOString(),
    };
    
    setEntries(prev => {
      const updated = [...prev, newEntry];
      // Save to localStorage
      const allData = JSON.parse(localStorage.getItem(NUTRITION_KEY) || '{}');
      allData[selectedDate] = updated;
      localStorage.setItem(NUTRITION_KEY, JSON.stringify(allData));
      // Sync to backend
      syncNutritionToBackend(selectedDate, updated);
      return updated;
    });
    
    // Check for high-carb meal and trigger walk reminder
    const carbs = parseFloat(entry.carbs) || 0;
    if (carbs >= 40 && selectedDate === getToday()) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('gym_notebook_auth_token');
      fetch(`${API_URL}/api/v2/walk-trigger/check-meal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          meal_name: entry.name || 'Meal',
          carbs: carbs,
          mealtime: entry.mealTime || selectedMealTime,
          date: selectedDate,
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.action === 'scheduled') {
            toast(data.message, {
              duration: 8000,
              icon: '🚶',
              style: { background: '#1e293b', color: '#f1f5f9', border: '1px solid #f59e0b' },
            });
          }
        })
        .catch(() => {}); // Silently fail - walk trigger is non-critical
    }
    
    toast.success(`Added ${entry.name}`);
    return true;
  }, [isExpired, selectedDate, selectedMealTime, syncNutritionToBackend]);

  const removeEntry = useCallback((entryId) => {
    setEntries(prev => {
      const updated = prev.filter(e => String(e.id) !== String(entryId));
      // Save to localStorage
      const allData = JSON.parse(localStorage.getItem(NUTRITION_KEY) || '{}');
      allData[selectedDate] = updated;
      localStorage.setItem(NUTRITION_KEY, JSON.stringify(allData));
      // Sync to backend
      syncNutritionToBackend(selectedDate, updated);
      return updated;
    });
    toast.success('Entry removed');
  }, [selectedDate, syncNutritionToBackend]);

  const updateEntry = useCallback((entryId, updates) => {
    setEntries(prev => {
      const updated = prev.map(e => e.id === entryId ? { ...e, ...updates } : e);
      // Save to localStorage
      const allData = JSON.parse(localStorage.getItem(NUTRITION_KEY) || '{}');
      allData[selectedDate] = updated;
      localStorage.setItem(NUTRITION_KEY, JSON.stringify(allData));
      // Sync to backend
      syncNutritionToBackend(selectedDate, updated);
      return updated;
    });
  }, [selectedDate, syncNutritionToBackend]);

  // ============ DATE NAVIGATION ============

  const changeDate = useCallback((days) => {
    setSelectedDate(prev => {
      const date = new Date(prev);
      date.setDate(date.getDate() + days);
      const newDate = formatDate(date);
      const today = getToday();
      
      // Don't go past today
      if (newDate > today) return prev;
      return newDate;
    });
  }, []);

  const goToToday = useCallback(() => {
    setSelectedDate(getToday());
  }, []);

  // ============ SAVED DATA MANAGEMENT ============

  const saveBarcode = useCallback((barcode, productData) => {
    setSavedBarcodes(prev => {
      const updated = { ...prev, [barcode]: productData };
      localStorage.setItem(SAVED_BARCODES_KEY, JSON.stringify(updated));
      syncBarcodesToBackend(updated);
      return updated;
    });
  }, [syncBarcodesToBackend]);

  const deleteBarcode = useCallback((barcode) => {
    setSavedBarcodes(prev => {
      const updated = { ...prev };
      delete updated[barcode];
      localStorage.setItem(SAVED_BARCODES_KEY, JSON.stringify(updated));
      syncBarcodesToBackend(updated);
      return updated;
    });
  }, [syncBarcodesToBackend]);

  const updateBarcode = useCallback((barcode, updates) => {
    setSavedBarcodes(prev => {
      if (!prev[barcode]) return prev;
      const updated = { ...prev, [barcode]: { ...prev[barcode], ...updates } };
      localStorage.setItem(SAVED_BARCODES_KEY, JSON.stringify(updated));
      syncBarcodesToBackend(updated);
      return updated;
    });
  }, [syncBarcodesToBackend]);

  const saveMeal = useCallback((name, items) => {
    const newMeal = {
      id: Date.now(),
      name,
      items,
      createdAt: new Date().toISOString(),
    };
    
    setSavedMeals(prev => {
      const updated = [...prev, newMeal];
      localStorage.setItem(SAVED_MEALS_KEY, JSON.stringify(updated));
      syncMealsToBackend(updated);
      return updated;
    });
    
    toast.success(`Saved meal: ${name}`);
    return newMeal;
  }, [syncMealsToBackend]);

  const deleteMeal = useCallback((mealId) => {
    setSavedMeals(prev => {
      const updated = prev.filter(m => m.id !== mealId);
      localStorage.setItem(SAVED_MEALS_KEY, JSON.stringify(updated));
      syncMealsToBackend(updated);
      return updated;
    });
    toast.success('Meal deleted');
  }, [syncMealsToBackend]);

  const renameMeal = useCallback((mealId, newName) => {
    setSavedMeals(prev => {
      const updated = prev.map(m => m.id === mealId ? { ...m, name: newName } : m);
      localStorage.setItem(SAVED_MEALS_KEY, JSON.stringify(updated));
      syncMealsToBackend(updated);
      return updated;
    });
    toast.success('Meal renamed');
  }, [syncMealsToBackend]);

  const addCustomMealTime = useCallback((name) => {
    const newMealTime = {
      id: `custom_${Date.now()}`,
      name,
      icon: '🍽️',
      isCustom: true,
    };
    
    setLocalCustomMealTimes(prev => {
      const updated = [...prev, newMealTime];
      localStorage.setItem(CUSTOM_MEAL_TIMES_KEY, JSON.stringify(updated));
      return updated;
    });
    
    toast.success(`Added meal time: ${name}`);
    return newMealTime;
  }, []);

  const saveCustomProduct = useCallback((product) => {
    setCustomProducts(prev => {
      const updated = [...prev, { ...product, id: Date.now() }];
      localStorage.setItem(CUSTOM_PRODUCTS_KEY, JSON.stringify(updated));
      return updated;
    });
    toast.success(`Saved product: ${product.name}`);
  }, []);

  // ============ BURNED CALORIES ============

  const updateBurnedCalories = useCallback((calories) => {
    setBurnedCalories(calories);
    setBurnedCaloriesOverride(true);
    
    // Save to localStorage
    const allData = JSON.parse(localStorage.getItem('gym_notebook_burned_calories') || '{}');
    allData[selectedDate] = calories;
    localStorage.setItem('gym_notebook_burned_calories', JSON.stringify(allData));
    
    // Sync to backend
    syncBurnedCaloriesToBackend(selectedDate, calories);
  }, [selectedDate, syncBurnedCaloriesToBackend]);

  const updateExternalSteps = useCallback((steps) => {
    setExternalSteps(steps);
    
    // Save to localStorage
    const allData = JSON.parse(localStorage.getItem(EXTERNAL_STEPS_KEY) || '{}');
    allData[selectedDate] = steps;
    localStorage.setItem(EXTERNAL_STEPS_KEY, JSON.stringify(allData));
    
    // Sync to backend
    syncExternalStepsToBackend(selectedDate, steps);
    
    // Update BMR data with external steps
    setBmrData(prev => ({
      ...prev,
      externalStepsActive: steps > 0,
      walkCalories: steps > 0 ? stepsToCalories(steps, bodyWeight) : prev.walkCalories,
    }));
  }, [selectedDate, bodyWeight, syncExternalStepsToBackend]);

  // ============ LOAD DATA ON DATE CHANGE ============

  useEffect(() => {
    const loadDataForDate = async () => {
      setIsLoading(true);
      
      // Load entries from localStorage first
      const localData = JSON.parse(localStorage.getItem(NUTRITION_KEY) || '{}');
      const localEntries = localData[selectedDate] || [];
      setEntries(localEntries);
      
      // Load burned calories from localStorage
      const burnedData = JSON.parse(localStorage.getItem('gym_notebook_burned_calories') || '{}');
      setBurnedCalories(burnedData[selectedDate] || 0);
      
      // Load external steps from localStorage
      const stepsData = JSON.parse(localStorage.getItem(EXTERNAL_STEPS_KEY) || '{}');
      setExternalSteps(stepsData[selectedDate] || 0);
      
      // Try to load from backend
      const backendData = await loadNutritionFromBackend(selectedDate);
      const backendEntries = backendData?.data?.entries || backendData?.entries;
      if (backendEntries?.length > 0) {
        setEntries(backendEntries);
        // Update localStorage with backend data
        localData[selectedDate] = backendEntries;
        localStorage.setItem(NUTRITION_KEY, JSON.stringify(localData));
      }
      
      // Load burned calories from backend
      const backendBurned = await loadBurnedCaloriesFromBackend(selectedDate);
      if (backendBurned !== null && backendBurned > 0) {
        setBurnedCalories(backendBurned);
        burnedData[selectedDate] = backendBurned;
        localStorage.setItem('gym_notebook_burned_calories', JSON.stringify(burnedData));
      }
      
      // Load external steps from backend
      const backendSteps = await loadExternalStepsFromBackend(selectedDate);
      if (backendSteps !== null && backendSteps > 0) {
        setExternalSteps(backendSteps);
        stepsData[selectedDate] = backendSteps;
        localStorage.setItem(EXTERNAL_STEPS_KEY, JSON.stringify(stepsData));
      }
      
      setIsLoading(false);
    };
    
    loadDataForDate();
  }, [selectedDate, loadNutritionFromBackend, loadBurnedCaloriesFromBackend, loadExternalStepsFromBackend]);

  // ============ LOAD GOALS & SAVED DATA ON MOUNT ============

  useEffect(() => {
    // Load goals
    const savedGoals = localStorage.getItem(NUTRITION_GOALS_KEY);
    if (savedGoals) {
      setGoals(JSON.parse(savedGoals));
    }
    
    // Load custom meal times
    const savedMealTimes = localStorage.getItem(CUSTOM_MEAL_TIMES_KEY);
    if (savedMealTimes) {
      setLocalCustomMealTimes(JSON.parse(savedMealTimes));
    }
    
    // Load saved barcodes
    const localBarcodes = localStorage.getItem(SAVED_BARCODES_KEY);
    if (localBarcodes) {
      setSavedBarcodes(JSON.parse(localBarcodes));
    }
    
    // Load saved meals
    const localMeals = localStorage.getItem(SAVED_MEALS_KEY);
    if (localMeals) {
      try {
        const parsed = JSON.parse(localMeals);
        // Clean up corrupted meals (where name is an object instead of string)
        const cleaned = parsed.filter(m => m && m.id && typeof m.name === 'string');
        if (cleaned.length !== parsed.length) {
          localStorage.setItem(SAVED_MEALS_KEY, JSON.stringify(cleaned));
        }
        setSavedMeals(cleaned);
      } catch {
        setSavedMeals([]);
      }
    }
    
    // Load custom products
    const localProducts = localStorage.getItem(CUSTOM_PRODUCTS_KEY);
    if (localProducts) {
      setCustomProducts(JSON.parse(localProducts));
    }
    
    // Load body weight from Personal > Body Info
    const bodyInfo = localStorage.getItem('gym_notebook_bodyweight');
    if (bodyInfo) {
      try {
        const parsed = JSON.parse(bodyInfo);
        setBodyWeight(parsed.weight || 70);
      } catch {
        setBodyWeight(70);
      }
    }
    
    // Try to load from backend
    const loadFromBackend = async () => {
      const backendBarcodes = await loadBarcodesFromBackend();
      if (backendBarcodes && Object.keys(backendBarcodes).length > 0) {
        // Merge: keep local barcodes, add/update with backend data
        const localData = JSON.parse(localStorage.getItem(SAVED_BARCODES_KEY) || '{}');
        const merged = { ...localData, ...backendBarcodes };
        setSavedBarcodes(merged);
        localStorage.setItem(SAVED_BARCODES_KEY, JSON.stringify(merged));
        // Re-sync merged set if local had extra items
        if (Object.keys(merged).length > Object.keys(backendBarcodes).length) {
          syncBarcodesToBackend(merged);
        }
      }
      
      const backendMeals = await loadMealsFromBackend();
      if (backendMeals && backendMeals.length > 0) {
        setSavedMeals(backendMeals);
        localStorage.setItem(SAVED_MEALS_KEY, JSON.stringify(backendMeals));
      }
    };
    
    loadFromBackend();
  }, [loadBarcodesFromBackend, loadMealsFromBackend, syncBarcodesToBackend]);

  // ============ BMR / TDEE / PROGRESSIVE GOAL CALCULATION ============

  const [bodyDataVersion, setBodyDataVersion] = useState(0);

  useEffect(() => {
    const handler = () => setBodyDataVersion(v => v + 1);
    window.addEventListener('bodyDataChanged', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('bodyDataChanged', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  useEffect(() => {
    // Load body data from localStorage
    const weightRaw = localStorage.getItem('gym_notebook_persistent_bodyweight');
    const weightUnitRaw = localStorage.getItem('gym_notebook_persistent_weight_unit') || 'kg';
    const heightRaw = localStorage.getItem('gym_notebook_persistent_height');
    const heightUnitRaw = localStorage.getItem('gym_notebook_persistent_height_unit') || 'cm';
    const ageRaw = localStorage.getItem('gym_notebook_persistent_age');
    const gender = localStorage.getItem('gym_notebook_persistent_gender') || 'male';
    const weightGoal = localStorage.getItem('gym_notebook_weight_goal') || 'maintain';
    const goalStartDate = localStorage.getItem('gym_notebook_weight_goal_start') || '';

    const weight = parseFloat(weightRaw);
    const height = parseFloat(heightRaw);
    const age = parseInt(ageRaw);

    if (!weight || !height || !age) {
      // Not enough data for BMR calculation — keep defaults
      setBmrData(prev => ({ ...prev, goal: weightGoal }));
      return;
    }

    // Convert to metric for BMR formula
    const weightKg = weightUnitRaw === 'lbs' ? convertWeight(weight, 'lbs', 'kg') : weight;
    // Height unit: 'cm' or 'ft' (stored as total inches when 'ft')
    const heightCm = heightUnitRaw === 'ft' || heightUnitRaw === 'in' 
      ? convertHeight(height, 'in', 'cm') : height;

    // Also update bodyWeight for step calorie calculations
    setBodyWeight(weightKg);

    // BMR (Mifflin-St Jeor)
    const bmr = calculateBMR(weightKg, heightCm, age, gender);
    const baseTDEE = calculateTDEE(bmr, 1.2); // sedentary base

    // Progressive goal adjustment
    const selectedDateObj = new Date(selectedDate + 'T00:00:00');
    const dayOfWeek = selectedDateObj.getDay(); // 0 = Sunday
    const isRelaxDay = dayOfWeek === 0;

    let weekNumber = 0;
    let calorieAdjustment = 0;
    let cycleWeek = 0;
    let cyclePhase = '';

    if (weightGoal === 'lose' && goalStartDate) {
      weekNumber = getGoalWeekNumber(goalStartDate, selectedDate);
      calorieAdjustment = calculateGoalAdjustment('lose', weekNumber, isRelaxDay);
      
      // Determine cycle phase for display
      const cycleLength = 20;
      cycleWeek = ((weekNumber - 1) % cycleLength) + 1;
      if (cycleWeek <= 2) cyclePhase = 'Start';
      else if (cycleWeek <= 10) cyclePhase = isRelaxDay ? 'Relax' : 'Active';
      else if (cycleWeek <= 12) cyclePhase = 'Taper';
      else cyclePhase = 'Maintain';
    }

    // Walk calories for the selected date
    const stepsData = JSON.parse(localStorage.getItem(EXTERNAL_STEPS_KEY) || '{}');
    const dateSteps = stepsData[selectedDate] || 0;
    const walkCal = dateSteps > 0 ? stepsToCalories(dateSteps, weightKg) : 0;

    // Final target: (baseTDEE + adjustment) + walkCalories
    const adjustedBaseTDEE = baseTDEE + calorieAdjustment;
    const dailyTarget = adjustedBaseTDEE + walkCal;

    setBmrData({
      bmr,
      activityLevel: 1.2,
      tdee: baseTDEE,
      goal: weightGoal,
      dailyTarget,
      walkCalories: walkCal,
      workoutCalories: 0,
      externalStepsActive: dateSteps > 0,
      calorieAdjustment,
      cycleWeek,
      cyclePhase,
      weekNumber,
    });
  }, [selectedDate, bodyDataVersion]);

  // ============ CONTEXT VALUE ============

  const value = {
    // Core state
    selectedDate,
    setSelectedDate,
    entries,
    goals,
    setGoals,
    burnedCalories,
    externalSteps,
    bodyWeight,
    bmrData,
    setBmrData,
    isExpired,
    isLoading,
    
    // Meal times
    allMealTimes,
    selectedMealTime,
    setSelectedMealTime,
    customMealTimes: localCustomMealTimes,
    addCustomMealTime,
    
    // Saved data
    savedBarcodes,
    saveBarcode,
    deleteBarcode,
    updateBarcode,
    savedMeals,
    saveMeal,
    deleteMeal,
    renameMeal,
    customProducts,
    saveCustomProduct,
    savedMenus,
    setSavedMenus,
    
    // Entry management
    addEntry,
    removeEntry,
    updateEntry,
    
    // Date navigation
    changeDate,
    goToToday,
    
    // Burned calories
    updateBurnedCalories,
    updateExternalSteps,
    burnedCaloriesOverride,
    
    // Sync functions (exposed for manual sync)
    syncNutritionToBackend,
    syncBarcodesToBackend,
    syncMealsToBackend,
  };

  return (
    <NutritionContext.Provider value={value}>
      {children}
    </NutritionContext.Provider>
  );
}

export function useNutrition() {
  const context = useContext(NutritionContext);
  if (!context) {
    throw new Error('useNutrition must be used within a NutritionProvider');
  }
  return context;
}

export default NutritionContext;
